import { useState, useEffect } from "react";
import UploadPage, { type ReconInputs } from "./pages/UploadPage";
import ResultPage from "./pages/ResultPage";
import LoginPage from "./pages/LoginPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import { api, setOn401, setStoreScope, setViewSegment } from "./api/client";
import { BookChoice, BookSwitch } from "./components/BookPicker";
import BrandMark from "./components/BrandMark";
import { resetColumnPins } from "./lib/columnPins";
import * as book_ from "./lib/book";
import * as sessionRestore from "./lib/sessionRestore";
import { applyTheme, getTheme, type ThemeChoice } from "./lib/theme";
import type { Book, ReconResult } from "./types/api";
import { DEFAULT_SIDEBAR, type SidebarSettings } from "./types/ui";

// Explicit labels + tooltips: the current theme is readable at a glance from which
// segment is highlighted, with no icon vocabulary to learn.
const THEME_OPTIONS: { value: ThemeChoice; label: string; hint: string }[] = [
  { value: "system", label: "Auto", hint: "Follow your computer's light/dark setting" },
  { value: "light", label: "Light", hint: "Always use the light theme" },
  { value: "dark", label: "Dark", hint: "Always use the dark theme" },
];

interface Session {
  result: ReconResult;
  prematchId: string;
  inputs: ReconInputs;
}

export default function App() {
  // The superadmin panel lives at /superadmin — a separate, unlinked surface with its
  // own login + role check. It is NOT gated here (the real gate is require_admin on the
  // backend); this only routes the URL to the right page. Computed before any hooks so
  // the early return below never changes hook order.
  const isSuperAdmin =
    typeof window !== "undefined" &&
    window.location.pathname.replace(/\/+$/, "") === "/superadmin";

  const [session, setSession] = useState<Session | null>(null);
  const [sidebar, setSidebar] = useState<SidebarSettings>(DEFAULT_SIDEBAR);
  // Which (insurer, CRI-span) the period defaults have already been seeded for. Lives
  // HERE, not in UploadPage, because Re-upload unmounts UploadPage: a ref inside it is
  // destroyed on the way back, the seed guard reopens, and the window the user had set
  // is silently overwritten by the insurer defaults on remount. Surviving the unmount
  // is the whole point — the defaults are a starting point, not a floor the UI keeps
  // re-imposing on every re-upload.
  const [periodSeedKey, setPeriodSeedKey] = useState("");
  // Which FX rates came from the user's SAVED set rather than the standing default.
  // Drives the sidebar notice: a rate persisted from an earlier session multiplies
  // every foreign-currency figure, so finance must never discover it only after the
  // numbers look wrong. Empty = everything is on the documented default.
  const [savedRates, setSavedRates] = useState<{ usd?: number; eur?: number }>({});
  // The carried-over-rate warning the user has acknowledged, stored as the exact rate
  // signature it was shown for (e.g. "usd:3.7|eur:4.9101"). Scoped to THIS SESSION and
  // to THAT value on purpose: a dismissal remembered across logins would let a rate
  // acknowledged in one closing period stay silently in force in the next, which is the
  // very thing the warning exists to catch. Any change to the saved rates yields a new
  // signature, so the warning returns for a fact the user has not checked yet.
  // Lives here, not in Sidebar: that component unmounts when moving between the upload
  // and dashboard screens, which would drop the acknowledgement mid-session.
  const [rateNoticeAck, setRateNoticeAck] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const toggleSidebar = () => setSidebarCollapsed((c) => !c);

  // Every sidebar edit funnels through here, so a rate the user commits is persisted
  // for their account from BOTH screens without each one re-implementing the save.
  // Only an actual CHANGE writes (the sidebar re-emits the whole settings object on any
  // field), and only a valid rate: a zero/blank box is mid-typing, and storing it would
  // hand the next session a rate that sign-flips or zeroes every converted figure.
  function updateSidebar(next: SidebarSettings) {
    const patch: { usd?: number; eur?: number } = {};
    if (next.usdRate !== sidebar.usdRate && next.usdRate > 0) patch.usd = next.usdRate;
    if (next.eurRate !== sidebar.eurRate && next.eurRate > 0) patch.eur = next.eurRate;
    if (Object.keys(patch).length) {
      setSavedRates((s) => ({ ...s, ...patch }));
      // Fire-and-forget: a failed save costs a re-type next session, never this run.
      api.setFxRates(patch).catch(() => {});
    }
    setSidebar(next);
  }
  // Rehydrating a refreshed session (see lib/sessionRestore). Gates the first paint so
  // a restorable session never flashes the upload screen on the way to the dashboard.
  const [restoring, setRestoring] = useState(() => !!sessionRestore.load());
  // Theme: three LABELLED options, not a cycling icon. A finance user should never
  // have to click an emoji to discover what it does, or guess which state they're in.
  // main.tsx already applied the stored value before first paint.
  const [theme, setTheme] = useState<ThemeChoice>(getTheme);
  function chooseTheme(next: ThemeChoice) {
    setTheme(next);
    applyTheme(next);
  }

  // Auth gate: on load, ask /api/me who we are. null = checking, "" = logged out.
  const [user, setUser] = useState<string | null>(null);
  const [canUseGemini, setCanUseGemini] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  // The reconciliation books this account holds, and the one it is working in.
  //
  // `book === null` means "not resolved yet": for a multi-book account that is the state
  // the chooser exists to settle, and it is deliberately NOT defaulted to AP — dropping
  // a dual-book user silently into AP lets them reconcile a whole month against the wrong
  // book's layouts and the figures still look real. A single-book account resolves
  // immediately and never sees a chooser or a switch, so an AP-only user's app is exactly
  // what it was before the AR book existed.
  //
  // An absent `books` (a server that predates the field) reads as AP, the same rule the
  // server applies to an account with no allowed_books.
  const [books, setBooks] = useState<Book[]>([book_.DEFAULT_BOOK]);
  const [book, setBook] = useState<Book | null>(null);
  // How many insurers are onboarded per book, for the chooser. Loaded only when the
  // chooser is actually shown, so a single-book account pays nothing for it.
  const [bookCounts, setBookCounts] = useState<Partial<Record<Book, number>>>({});

  /** Take the book scope /api/me reports and settle which book we open in.
   *
   *  ABSENT and EMPTY are not the same thing, and conflating them would widen an
   *  account: `undefined` is a server that predates the field, which means AP (the rule
   *  the server itself applies to an account with no allowed_books), whereas an empty
   *  list is the server stating this account holds NO book — which must stay no book, so
   *  the user is told that plainly instead of being shown AP and then 403'd. */
  function adoptBooks(held?: Book[]) {
    const list = held ?? [book_.DEFAULT_BOOK];
    setBooks(list);
    setBook(book_.resolveBook(list, book_.loadBook()));
  }

  /** Commit a book the user picked. Clears the chosen book's session-scoped state so
   *  nothing from the other book leaks across: the stored session belongs to one book,
   *  and the column pins / period seed were derived from that book's frames. */
  function chooseBook(next: Book) {
    if (next === book) return;
    book_.saveBook(next);
    setBook(next);
    setSession(null);
    setStoreScope(null);   // the other book's store scope must not survive the switch
    setViewSegment("");
    sessionRestore.clear();
    resetColumnPins();
    setPeriodSeedKey("");
  }
  useEffect(() => {
    if (isSuperAdmin) return;  // the superadmin page runs its own /api/me check
    // A 401 on any gated call (session expired/revoked) drops back to login.
    // Pins are cached per user in-process; drop them so the next login never
    // inherits the previous user's preferences on a shared machine.
    setOn401(() => {
      setUser(""); setSession(null); resetColumnPins(); sessionRestore.clear();
      setRateNoticeAck("");   // session-scoped, like the pins above
      book_.clearBook();      // ditto: the next user of this tab chooses their own book
      setStoreScope(null);    // and no store call may carry the dead session's scope
      setViewSegment("");
    });
    api.me()
      .then((r) => { setUser(r.username); setCanUseGemini(!!r.can_use_gemini); adoptBooks(r.books); })
      .catch(() => setUser(""))
      .finally(() => setAuthChecked(true));
  }, [isSuperAdmin]);

  // Adopt the user's SAVED FX rates. Runs once auth is confirmed (the endpoint is
  // session-gated) and only when no reconciliation is already on screen, so a restored
  // session keeps the rates it actually ran with. A currency absent from `saved` has
  // nothing stored and correctly keeps the standing default — an absent rate is never
  // read as zero. Any failure leaves the defaults in place: a saved rate is a
  // convenience, and guessing one would corrupt every converted figure.
  useEffect(() => {
    if (isSuperAdmin || !authChecked || !user || session) return;
    let stale = false;
    api.fxRates()
      .then((r) => {
        if (stale || !r.saved) return;
        setSavedRates(r.saved);
        setSidebar((s) => ({
          ...s,
          usdRate: r.saved.usd ?? s.usdRate,
          eurRate: r.saved.eur ?? s.eurRate,
        }));
      })
      .catch(() => { /* defaults stand */ });
    return () => { stale = true; };
  }, [authChecked, user, isSuperAdmin, session]);

  // Restore a reconciliation the user left behind (F5 / tab close / sleep). The
  // PrematchState is still in Redis (a day of idle time), so /finalize rebuilds the FULL result from
  // the stored prematch_id in ~0.4s — far cheaper than the re-upload + ~10s re-run this
  // replaces. Runs only once auth is confirmed, since /finalize is session-gated.
  // Any failure (409 expired, network, stale payload) silently drops to the upload
  // screen: restore is a convenience and must never block getting back to work.
  useEffect(() => {
    if (isSuperAdmin || !authChecked || !user || session) return;
    const saved = sessionRestore.load();
    if (!saved) { setRestoring(false); return; }
    let stale = false;
    setRestoring(true);
    // Before the finalize, so the restored figures are the slice that was on screen.
    setViewSegment(saved.segment ?? "");
    api.finalize({
      prematchId: saved.prematchId,
      insurerName: saved.insurerName,
      sessionPairs: saved.sessionPairs,
      manualNotesCri: saved.notesCri,
      manualNotesInsurer: saved.notesInsurer,
      // Figures first, exactly as a fresh run does: the restore screen is a spinner over
      // a 74 MB download otherwise, and ResultPage collects the rows once it mounts.
      includeFrames: false,
    })
      .then((r) => {
        if (stale) return;
        setSidebar(saved.sidebar);
        // The restored result belongs to the book it was RUN in, so adopt that book
        // rather than whatever this tab resolved to — otherwise a refresh could show one
        // book's figures under the other book's label. Absent = AP, as everywhere else.
        setBook(saved.book ?? book_.DEFAULT_BOOK);
        // Re-establish the store scope the run was made under, so the restored
        // dashboard loads THIS customer's remembered matches and not another's.
        setStoreScope({ book: saved.book ?? book_.DEFAULT_BOOK,
                        customerKey: saved.customerKey });
        setSession({
          result: r.result,
          prematchId: saved.prematchId,
          inputs: {
            criFiles: [],           // not serialisable — see ResultPage.filesLost
            insurerFiles: [],
            insurerName: saved.insurerName,
            insurerSheetName: saved.insurerSheetName,
            book: saved.book ?? book_.DEFAULT_BOOK,
            settings: saved.reconSettings,
            restored: {
              sessionPairs: saved.sessionPairs ?? [],
              notesCri: saved.notesCri ?? {},
              notesInsurer: saved.notesInsurer ?? {},
              bridgeRejections: saved.bridgeRejections ?? [],
            },
          },
        });
      })
      .catch(() => { if (!stale) sessionRestore.clear(); })
      .finally(() => { if (!stale) setRestoring(false); });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked, user, isSuperAdmin]);

  // Insurer counts for the chooser. Runs only while the chooser is on screen (book
  // unresolved and more than one book held), so a single-book account never pays for it.
  // Each book is asked separately because each has its own layouts; a failure just
  // leaves that card's line blank — the count is context, never a gate on choosing.
  useEffect(() => {
    if (isSuperAdmin || !authChecked || !user || book || books.length < 2) return;
    let stale = false;
    for (const b of books) {
      api.insurers(b)
        .then((r) => {
          if (!stale) setBookCounts((c) => ({ ...c, [b]: r.insurers.length }));
        })
        .catch(() => { /* the card simply shows no count */ });
    }
    return () => { stale = true; };
  }, [isSuperAdmin, authChecked, user, book, books]);

  if (isSuperAdmin) {
    return <SuperAdminPage />;
  }

  async function logout() {
    // Drop the restorable session too: a sign-out must leave nothing for the next
    // user of a shared machine (same contract as resetColumnPins).
    sessionRestore.clear();
    // Drop the acknowledgement too: it is scoped to one session, so the next user of a
    // shared machine (or the same user next period) must see the rate warning afresh.
    setRateNoticeAck("");
    // Same reason: the next user of this tab must choose their own book, not inherit one.
    book_.clearBook();
    try { await api.logout(); } finally {
      setUser(""); setSession(null); resetColumnPins(); setBook(null); setBookCounts({});
      setStoreScope(null);
      setViewSegment("");
    }
  }

  if (!authChecked || (restoring && user)) {
    return (
      <div className="muted center-screen">
        <span className="spinner" />{restoring && user ? "Restoring your reconciliation…" : "Loading…"}
      </div>
    );
  }
  if (!user) {
    return <LoginPage onLoggedIn={(u) => {
      setUser(u);
      // Pull the Gemini permission and the book scope for the freshly-logged-in user
      // (they drive the upload UI and the book chooser). A failure leaves the account on
      // AP only, which can never widen anyone.
      api.me()
        .then((r) => { setCanUseGemini(!!r.can_use_gemini); adoptBooks(r.books); })
        .catch(() => { setCanUseGemini(false); adoptBooks(); });
    }} />;
  }

  // An account holding no book at all can do nothing, and every gated call would 403.
  // Say so plainly instead of showing an empty chooser or a broken upload form.
  if (!books.length) {
    return (
      <div className="muted center-screen">
        Your account has no reconciliation book assigned. Ask an administrator to grant
        you Accounts Payable or Accounts Receivable access.
      </div>
    );
  }

  return (
    <div data-book={book ?? undefined}>
      <header className="app-header">
        <div className="header-left">
          <div className="brand-lockup">
            <BrandMark />
            <span className="brand-divider" aria-hidden="true" />
            <strong className="app-brand">Reconciliation</strong>
          </div>
          {book && book_.showsBookSwitch(books) && (
            <BookSwitch books={books} book={book} guarded={!!session}
              onPick={chooseBook} />
          )}
        </div>
        <div className="header-right">
          <span className="muted">{user}</span>
          <span className="muted header-label">Theme</span>
          <div className="theme-switch" role="radiogroup" aria-label="Colour theme">
            {THEME_OPTIONS.map(({ value, label, hint }) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={theme === value}
                className={`theme-opt ${theme === value ? "active" : ""}`}
                title={hint}
                onClick={() => chooseTheme(value)}
              >{label}</button>
            ))}
          </div>
          <button className="btn" onClick={logout}>Sign out</button>
        </div>
      </header>
      {!book ? (
        // Multi-book account, nothing chosen yet. The chooser stands in for the upload
        // screen rather than overlaying it: the book selects which insurers, layouts and
        // stores the form would load, so there is nothing meaningful to render behind it.
        <BookChoice books={books} counts={bookCounts} onPick={chooseBook} />
      ) : session ? (
        <ResultPage
          result={session.result}
          prematchId={session.prematchId}
          inputs={session.inputs}
          sidebar={sidebar}
          onSidebarChange={updateSidebar}
          savedRates={savedRates}
          rateNoticeAck={rateNoticeAck}
          onAckRateNotice={setRateNoticeAck}
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={toggleSidebar}
          onReupload={() => { sessionRestore.clear(); setViewSegment(""); setSession(null); }}
        />
      ) : (
        <UploadPage
          settings={sidebar}
          onSettingsChange={updateSidebar}
          savedRates={savedRates}
          rateNoticeAck={rateNoticeAck}
          onAckRateNotice={setRateNoticeAck}
          periodSeedKey={periodSeedKey}
          onPeriodSeeded={setPeriodSeedKey}
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={toggleSidebar}
          onReconciled={(resp, inputs) =>
            {
              // Scope every per-insurer store call to THIS reconciliation before the
              // dashboard mounts and starts loading remembered matches.
              setStoreScope({ book: resp.result.recon_book,
                              customerKey: resp.result.customer_key });
              setViewSegment("");   // a fresh run opens on All business
              setSession({ result: resp.result, prematchId: resp.prematch_id, inputs });
            }
          }
          canUseGemini={canUseGemini}
          book={book}
          user={user}
        />
      )}
    </div>
  );
}
